import React, { Fragment, Suspense, useEffect, useState } from "react";
//react query
import { ReactQueryDevtools } from "react-query/devtools";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import * as Sentry from "@sentry/react";

import "./App.scss";
import ErrorBoundary from "./components/ErrorBoundary";

import { DataPivot } from "./components/analysisModules/DataPivot";
import WarningScreen from "./components/generalUI/WarningScreen";
import { Footer } from "./components/nav/Footer";
import { PrivateRoute } from "./components/routing/PrivateRoute";
import RouteAccessModule from "./components/routing/RouteAccessModule";
import RouteAccessOneContent from "./components/routing/RouteAccessOneContent";
import RouteAccessOneProfile from "./components/routing/RouteAccessOneProfile";
import RouteAccessSomeContent from "./components/routing/RouteAccessSomeContent";
import RouteSuperAdmin from "./components/routing/RouteSuperAdmin";
import RouteSomeAccessToContentType from "./components/routing/RouteSomeAccessToContentType"
import { AppSettingsProvider } from "./contexts/appSettingsContext";
import ChooseTemplate from "./pageTpls/ChooseTemplate";

import {
  BannerAlert,
  BannerInfo,
  GlobalStyles,
  Loader,
  LoaderCircleV2,
  UI_BODY_SEMIBOLD_DF,
  UI_BODY_SM_DF,
  useBannerContext,
} from "oolib";
import OolibThemeProvider from "./OolibThemeProvider";
import { useOnlineStatusChange } from "./utils/environmentConfig";

import { useGetPlatformConfigs } from "./utils/react-query-hooks/useGetPlatformConfigs";
import { useUserLoad } from "./utils/react-query-hooks/auth";

import { Coachmark } from "./components/CoachmarkV2";

import { hotjar } from "react-hotjar";

import { Header } from "./components/nav/Header";

import { useGetTplsAndCacheSeparately } from "./utils/react-query-hooks/tpls";
import { LocalhostTenantSwitcher } from "./components/LocalhostTenantSwitcher";
import { useSetAppropriateMetaTitle } from "./utils/customHooks/useSetAppropriateMetaTitle";
import { useActivateGoogleAnalytics } from "./trackers/GA/useActivateGoogleAnalytics";
import PluginRoutes from "./Plugins/routes";
import { PreAccessChecklistContextProvider } from "./contexts/preAccessChecklistContext";
import RouteSomeAccessSomePlatformBuilderModule from "./components/routing/RouteSomeAccessSomePlatformBuilderModule";
import { getAccessControlEnabledPLBModules } from "./pageTpls/PlatformBuilder/config";
import { useQueryClient } from "react-query";
import MomentLocale from "./components/locale/MomentLocale";
import { LangSelectModal } from "./LangSelectModal";
import { InsightMatrix } from "./components/analysisModules/InsightMatrix";

import { AnnotationExplorer } from "./components/analysisModules/AnnotationExplorer";
import BroadcastNavigator from "./components/BroadcastNavigator";
import SelfServeSurvey from "./pageTpls/SelfServeSurvey";
import mixpanel from "mixpanel-browser";
import { getTenant } from "./TENANT_CONFIGS";
import { ReportLanding } from "./components/analysisModules/reports/ReportLanding";
import { ReportsListing } from "./components/analysisModules/reports/ReportsListing";
import { useHeaderHeight } from "./CONSTANTS";

import { BatchRegisterPage } from "./pageTpls/PlatformBuilder/comps/BatchRegisterPage";
import { parseSpecialConfigSyntax } from "@okf/ootils";
import {SelfManagedRoutesRenderer} from './_self_managed/SelfManagedRoutesRenderer'
import { DirectDataImportOAuthReturnHandler } from "./_self_managed/DirectDataImport/DirectDataImportOAuthReturnHandler";
import GlobalSearch from "./components/discovery/GlobalSearch";
import { QueueJobsProgressFeedback } from "./components/QueueJobsProgressFeedback";
import { AutoAnnotateProgressFeedback } from "./components/AutoAnnotateProgressFeedback";
import { FiltersPlayground } from "./pageTpls/Playground/pages";

// ----------- SELF MANAGED MODE ROUTES ----------- //
const SM_Home = React.lazy(() => import("./_self_managed/SM_Home"));
const SM_DataImport = React.lazy(() => import("./_self_managed/SM_DataImport"));
const SM_TCI = React.lazy(() => import("./_self_managed/SM_TCI"))
const SM_PublishedListing = React.lazy(() => import("./_self_managed/SM_PublishedListing"));
const SM_PublishedPage = React.lazy(() => import("./_self_managed/SM_PublishedPage"));
const Analysis = React.lazy(() => import("./_self_managed/SM_Analysis"));
const TagsManagerV2 = React.lazy(() => import("./pageTpls/PlatformBuilder/subModules/TagsManagerV2"));
const DirectDataImport = React.lazy(() => import("./_self_managed/DirectDataImport"));
// ----------- END SELF MANAGED MODE ROUTES ----------- //
 

// import { getVal } from '@okf/ootils';

// const data = {
//   hello: 'hi'
// }

// console.log({testootils: getVal(data, 'hello')})

// console.log("REACT VERSION: ", React.version);
const PlatformBuilder = React.lazy(() => import("./pageTpls/PlatformBuilder"));
// const ContentTypeLanding = React.lazy(() => import('./pageTpls/PlatformBuilder/subModules/ContentTypesManager/comps/ContentTypeLanding'))
const Synthesis = React.lazy(() => import("./components/Synthesis"));
const TCIFetch = React.lazy(() =>
  import("./pageTpls/TCI/TCIFetch")
);

const Home = React.lazy(() => import("./pageTpls/Home"));
const AiChat = React.lazy(() => import("./components/AiChat"));
const OverviewDashboard = React.lazy(() => import("./components/analysisModules/OverviewDashboard"));
const Playground = React.lazy(() => import("./pageTpls/Playground"));

const ContributeConfigure = React.lazy(() =>
  import("./pageTpls/ContributeConfigure")
);

const ContributeTpl = React.lazy(() => import("./pageTpls/ContributeTpl"));
const ViewProfileUser = React.lazy(() =>
  import("./pageTpls/ViewProfile/ViewProfileUser")
);

const UserProfilesListing = React.lazy(() =>
  import("./pageTpls/ProfilesListing")
);

const PublishedListing = React.lazy(() =>
  import("./pageTpls/PublishedListing")
);
const PublishedPage = React.lazy(() => import("./pageTpls/PublishedPage"));

const ModQueue = React.lazy(() => import("./pageTpls/ModQueue"));

const OKE404 = React.lazy(() => import("./pageTpls/OKE404"));
const Onboarding = React.lazy(() => import("./pageTpls/Onboarding"));
const UserAgreement = React.lazy(() => import("./pageTpls/UserAgreement"));
const GaAgreement = React.lazy(() => import("./pageTpls/GaAgreement"));

//----my dashboard -------//
const MyDashboard = React.lazy(() => import("./pageTpls/MyDashboard"));

const MyProfileEdit = React.lazy(() => import("./pageTpls/MyProfileEdit"));

/**
 * ---- auth routes ------- |
 */
const Signup = React.lazy(() => import("./pageTpls/Signup"));
const Login = React.lazy(() => import("./pageTpls/Login"));
const PublicReport = React.lazy(() => import("./pageTpls/PublicReport"));
const ForgotPassword = React.lazy(() =>
  import("./pageTpls/auth/ForgotPassword")
);
const ResetPassword = React.lazy(() => import("./pageTpls/auth/ResetPassword"));
const AccountVerification = React.lazy(() =>
  import("./pageTpls/AccountVerification")
);
const GoogleAuth = React.lazy(() => import("./pageTpls/GoogleAuth"));
/**
 * ---- END auth routes ---- |
 */


const App = () => {

  //init hooks
  //phase this out, once we shift listings to react query
  // const { envConfigLoading } = useSelector((s) => s.environment)
  const { data: platformConfigs, status: platformConfigsStatus } =
    useGetPlatformConfigs();

  // fetch all tpls, and cache them separately as well
  const queryClient = useQueryClient();
  const { data: getTplsData, status: getTplsStatus } =
    useGetTplsAndCacheSeparately();
  /**
   * why are we doing this complicated stuff below rather than simply checking getTplsStatus and
   * load app accordingly?
   * Answer: we are being super safe here. if we rely on getTplsStatus, that will be 'success'
   * once the combined 'all tpls' cache is created. But, what we really care about is the
   * 'separately cached' tpls, and 'success' status doesnt tell us when that is done, since that
   * will be completed a fractional few milliseconds later (since those caches are set in the
   * onSuccess callback). Hence we use this function below, instead of relying on getTplsStatus
   */
  const atLeastOneSeparateTplCacheExists = (getTplsData) => {
    if (!getTplsData) return false;
    if (getTplsData.length === 0) return true; //basically, if no templates exist yet on the platform, we still want the app to load
    if (!!queryClient.getQueryData(["tpl", getTplsData[0].kp_content_type]))
      return true;
    // (above) : basically if at least one tpl cache is set, its a good enough proxy to know if all tpl caches are set.
    //else
    return false;
  };

  // banner state
  const { BANNER_STATE, REMOVE_ALERT_BANNER, REMOVE_INFO_BANNER } =
    useBannerContext();

  
  const userData = useUserLoad({
    platformConfigsLoading: platformConfigsStatus === "loading",
    platformConfigs,
    enabled: getTplsStatus === "success",
  });
  const enableCoachmark =
    platformConfigs?.deployment?._EnableCoachmarks?.enable;

  useOnlineStatusChange();

  /**
   * PWA enabled apps, serve static files like index.html from serviceworker cache
   * Hence, we dont get the opportunity to dynamically edit the meta tags as per tenant, in index.html
   * on the server side.
   *
   * So we have to run this fix so that '__META_TITLE__' doesnt show up in the browser tab
   */
  useSetAppropriateMetaTitle();

  useEffect(() => {
    if (platformConfigsStatus === "success") {
      // const {_Branch} = platformConfigs;
      let _Hotjar;
      if(platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.enable){
        _Hotjar = platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.hotjarConfig
      }else{
        _Hotjar = platformConfigs.deployment._Hotjar
      }
      
      // only if its the live production version of the platform
      if (
        import.meta.env.VITE_ENV === "prod" &&
        _Hotjar?.enable &&
        process.env.NODE_ENV === "production"
      ) {
        hotjar.initialize(_Hotjar.id, _Hotjar.sv);
      }
    }

    if (userData.status === "success") {
      Sentry.setUser({
        email: userData.data.user.email,
        username: userData.data.user.name,
        ip_address: "{{auto}}",
      });
    }
  }, [userData.status, platformConfigsStatus]);


  useEffect(() => {
    if (platformConfigsStatus === "success") {
      
       
        mixpanel.init("53bcd176c00b86330d070d59e5a47725", {
          debug: true,
          track_pageview: "url-with-path-and-query-string",
          track_links: true,
          track_forms: true,
          persistence: "localStorage",
          autocapture: false //(process.env.NODE_ENV === 'production' && import.meta.env.VITE_ENV === 'prod')
            // ? true
            // : false
        });
        
        // register "tenant" as a super property
        mixpanel.register({
          '$tenant_id': getTenant(),
          '$tenant_launchDate': '<pending>',
          '$tenant_renewalDate': '<pending>',
          '$tenant_size': '<pending>',
          '$environment': process.env.NODE_ENV !== "production"
            ? 'local'
            : import.meta.env.VITE_ENV
        });

        // track the "registration" event above
        // mixpanel.track('registration');
    

    if (userData.status === "success") {
      
      mixpanel.identify(userData.data.user._id) 
      mixpanel.people.set({ 
        '$name': userData.data.user.name,
        '$email': userData.data.user.email,
        '$role': userData.data.user.role,
      });
      
    }
  }
  }, [userData.status, platformConfigsStatus]);

  useActivateGoogleAnalytics({
    platformConfigs,
    platformConfigsStatus,
    userData: userData.status === "success" ? userData.data : undefined,
  });

  const headerHeight = useHeaderHeight();

  const [showWarningScreen, setShowWarningScreen] = useState(true);
  // useEffect(() => {
  // 	function findRects(){
  // 		const rects = coachMarkConfig[1]?.elements?.map((element) => {
  // 			const htmlElement = document.getElementById(element?.id);
  // 			return { rect: htmlElement?.getBoundingClientRect(), element: element?.element }
  // 		})

  // 		console.log({ rectsOnHome: rects })
  // 	}

  // 	window.addEventListener('scroll', findRects);
  //     window.addEventListener('resize', findRects);

  //     return () => {
  //       window.removeEventListener('scroll', findRects);
  //       window.removeEventListener('resize', findRects);
  //     };

  // }, [])
  const warningScreen =
    import.meta.env.VITE_ENV === "staging" && showWarningScreen ? true : false;

  const enableHeaderOnlyIfLogged = platformConfigs?.deployment?._EnableHeaderOnlyIfLogged?.enable

  return (
    <BrowserRouter>
      <PreAccessChecklistContextProvider>
        <ReactQueryDevtools
          initialIsOpen={false}
          toggleButtonProps={{ style: { right: 0, left: "unset" } }}
        />
        {platformConfigsStatus !== "success" ||
        !atLeastOneSeparateTplCacheExists(getTplsData) ||
        userData.status === "loading" ? (
          <div>
            <LoaderCircleV2 blockHeight="100vh" />
          </div>
        ) : (
          <Fragment>
            <AppSettingsProvider>
              <MomentLocale />
              <OolibThemeProvider>
                {(process.env.NODE_ENV !== "production" ||
                  window.location.origin.includes(
                    "-dot-okf-fe-dev-dot-ok-framework.el.r.appspot.com"
                  )) && (
                  //|| import.meta.env.VITE_ENV === 'dev' activate when making dev a single url
                  <LocalhostTenantSwitcher />
                )}
                {/** has to be here, and not in index, because it uses platformConfigs API */}
                {import.meta.env.VITE_ENV === "staging" &&
                  showWarningScreen && (
                    <WarningScreen
                      platformConfigs={platformConfigs}
                      onClose={() => setShowWarningScreen(false)}
                    />
                  )}

                  <Fragment>
                    <GlobalStyles />
                    {
                      platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.enable === true  &&
                      // Single fixed-positioned stack container for all queue
                      // activity feedback cards. Children stack via flex
                      // column-reverse — each component just emits its card(s),
                      // the container handles positioning.
                      <div
                        style={{
                          zIndex: 100000,
                          position: "fixed",
                          right: "16px",
                          bottom: process.env.NODE_ENV !== "production"
                            ? "60px" // local dev has the react-query devtools + tenant switcher buttons at bottom-right
                            : "16px",
                          display: "flex",
                          flexDirection: "column-reverse",
                          gap: "12px",
                        }}
                      >
                        <QueueJobsProgressFeedback
                          // testMode={{ contentType: "anything", state: "imported", tplTitle: "Articles About Indian Subculture and Post Colonial Resurgence IDI 3" }}
                        />
                        <AutoAnnotateProgressFeedback
                          // testMode={{ contentType: "anything", state: "completed", tplTitle: "Articles" }}
                        />
                      </div>
                    }
                    <div id="optionsLightbox-root"></div>
                    <div id="annoLightbox-root"></div>
                    <div id="modal-root"></div>
                    {/* we use createPortal to render modals, this div is used as refernce node*/}
                    <div id="actionmenu-lightbox-root" />
                    {/** we use createProtal to render the actionmenu lightbox here IF its prop 'popOutOfOverflowHiddenParent' is set to true */}
                    <div id="loader-root" />
                    <BannerInfo
                      variant={BANNER_STATE?.variant}
                      BANNER_STATE={BANNER_STATE}
                      REMOVE_INFO_BANNER={REMOVE_INFO_BANNER}
                    />
                    { 
                    //self managed uses a primary sidebar nav so dont need header
                    platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.enable !== true && 
                    (enableHeaderOnlyIfLogged ?
                      (
                        userData?.data ? (
                          <Header />
                        ) : (
                          <></>
                        )
                      ) : 
                      
                      (
                        <Header />
                      ))
                    
                    }
                    <LangSelectModal />
                    <BroadcastNavigator />
                    <DirectDataImportOAuthReturnHandler />
                    <BannerAlert
                      BANNER_STATE={BANNER_STATE}
                      REMOVE_ALERT_BANNER={REMOVE_ALERT_BANNER}
                    />
                    <div className="kp-content">
                     

                        {platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.enable === true 
                        ? <SelfManagedRoutesRenderer thisUser={userData?.data?.user} SELF_MANAGED_BASE_CONFIGS={platformConfigs?.SELF_MANAGED_BASE_CONFIGS}/>
                        : <ErrorBoundary>
                        <Suspense
                        fallback={
                          <LoaderCircleV2
                            blockHeight={`calc(100vh - ${headerHeight})`}
                          />
                        }
                      >
                        <Routes>
                          {/* Plugin Routes */}
                          <Route path="/plugins/*" element={<PluginRoutes />} />

                          {/* Home Route */}
                          <Route
                            path="/"
                            element={
                              <RouteAccessModule ACTION="READ" moduleId="home">
                                <Home />
                              </RouteAccessModule>
                            }
                          />

                          {/* Private Routes */}
                          <Route
                            path="/playground"
                            element={
                              <PrivateRoute>
                                <Playground />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/filters-playground"
                            element={
                              <PrivateRoute>
                                <FiltersPlayground />
                              </PrivateRoute>
                            }
                          />

                           <Route
                            path="/overview-dashboard"
                            element={
                              <PrivateRoute>
                                <OverviewDashboard />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/aiChat"
                            element={
                              <PrivateRoute>
                                <AiChat />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/my-dashboard"
                            element={
                              <PrivateRoute>
                                <MyDashboard />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/my-dashboard/:SectionId"
                            element={
                              <PrivateRoute>
                                <MyDashboard />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/contribute-config/:contentType"
                            element={
                              <PrivateRoute>
                                <ContributeConfigure />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/choose-template"
                            element={
                              <PrivateRoute>
                                <ChooseTemplate />
                              </PrivateRoute>
                            }
                          />

                          {/* Auth Routes */}
                          <Route path="/login" element={<Login />} />
                          <Route path="/signup" element={<Signup />} />
                          <Route
                            path="/verifyEmail/:token"
                            element={<AccountVerification />}
                          />
                          <Route
                            path="/reset-password"
                            element={
                              <PrivateRoute>
                                <ResetPassword />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/googleAuth/:token"
                            element={<GoogleAuth />}
                          />
                          <Route
                            path="/verifyResetPasswordMail"
                            element={<ForgotPassword />}
                          />
                          <Route
                            path="/verifyResetPasswordMail/:token"
                            element={<ForgotPassword />}
                          />
                          {/* Content Routes */}
                          <Route
                            path="/published-listing/:contentType"
                            element={
                              <RouteAccessOneContent
                                ACTION="READ_IF_MEMBER"
                                ignoreExtendedIsMemberCheck={true}
                              >
                                <PublishedListing />
                              </RouteAccessOneContent>
                            }
                          />

                          <Route
                            path="/published-page/:content_type"
                            element={<PublishedPage />}
                          />

                          <Route
                            path="/edit/:content_type"
                            element={
                              <RouteSomeAccessToContentType ACTION={["UPDATE","PUBLISH","SUBMIT"]}>
                                <ContributeTpl />
                              </RouteSomeAccessToContentType>
                            }
                          />

                          {/* Moderation Routes */}
                          <Route
                            path="/moderate/:content_type"
                            element={
                              <RouteAccessOneContent ACTION="MODERATE">
                                <ContributeTpl />
                              </RouteAccessOneContent>
                            }
                          />

                          <Route
                            path="/mod-queue"
                            element={
                              <RouteAccessSomeContent ACTION="MODERATE">
                                <ModQueue />
                              </RouteAccessSomeContent>
                            }
                          />

                          {/* Analysis Routes */}
                          <Route
                            path="/synthesisV2"
                            element={
                              <RouteAccessModule
                                moduleId="analysisDashboard"
                                ACTION="READ"
                              >
                                <Synthesis />
                              </RouteAccessModule>
                            }
                          />

                          <Route
                            path="/insightMatrix"
                            element={
                              <RouteAccessSomeContent ACTION="READ">
                                <InsightMatrix />
                              </RouteAccessSomeContent>
                            }
                          />

                          <Route
                            path="/reports"
                            element={
                              
                              <RouteAccessModule ACTION="READ" moduleId="reports">
                                <ReportsListing />
                              </RouteAccessModule>
                            }
                          />

                          {/* /view is the one way in; a legacy /edit URL redirects to it
                              inside ReportLanding. Editing is PUBLISH access resolved by
                              the collab session, not a URL. */}
                          <Route
                            path="/reports/:mode/:reportId"
                            element={
                              <RouteAccessModule moduleId="reports" ACTION="READ">
                                <ReportLanding />
                              </RouteAccessModule>
                            }
                          />

                          {/* PUBLIC "share for all to view" — no auth guard. Renders a
                              read-only snapshot of a shared report by its link token. */}
                          <Route
                            path="/public/report/:token"
                            element={<PublicReport />}
                          />

                          <Route
                            path="/annotation-explorer"
                            element={
                              <RouteAccessSomeContent ACTION="READ">
                                <AnnotationExplorer />
                              </RouteAccessSomeContent>
                            }
                          />

                          <Route
                            path="/global-search"
                            element={
                              <RouteAccessSomeContent ACTION="READ">
                                <GlobalSearch />
                              </RouteAccessSomeContent>
                            }
                          />

                          <Route
                            path="/dataPivot"
                            element={
                              <RouteAccessSomeContent ACTION="READ">
                                <DataPivot />
                              </RouteAccessSomeContent>
                            }
                          />

                          {/* Profile Routes */}
                          <Route
                            path="/profile/userProfiles/:profileType/:id"
                            element={<ViewProfileUser />}
                          />

                          <Route
                            path="/community-listing/:profileType"
                            element={
                              <RouteAccessOneProfile ACTION="READ">
                                <UserProfilesListing />
                              </RouteAccessOneProfile>
                            }
                          />

                          <Route
                            path="/contribute-config"
                            element={
                              <PrivateRoute>
                                <ContributeConfigure />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/my-edit/:profileType/:id"
                            element={
                              <PrivateRoute>
                                <MyProfileEdit />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/onboarding"
                            element={
                              <PrivateRoute>
                                <Onboarding />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/self-serve-survey"
                            element={
                              <PrivateRoute>
                                <SelfServeSurvey />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/userAgreement"
                            element={
                              <PrivateRoute>
                                <UserAgreement />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/gaAgreement"
                            element={
                              <PrivateRoute>
                                <GaAgreement />
                              </PrivateRoute>
                            }
                          />

                          {/* TCI Routes */}
                          <Route
                            path="/tci/:contentType"
                            element={
                              <RouteSuperAdmin>
                                <TCIFetch />
                              </RouteSuperAdmin>
                            }
                          />

                          {/* Platform Builder Routes */}
                          <Route
                            path="/platformBuilder"
                            element={
                              <RouteSomeAccessSomePlatformBuilderModule
                                ACTIONS={["READ", "UPDATE"]}
                                modules={getAccessControlEnabledPLBModules().map(
                                  (d) => d.value
                                )}
                              >
                                <PlatformBuilder />
                              </RouteSomeAccessSomePlatformBuilderModule>
                            }
                          />

                          <Route
                            path="/platformBuilder/:view"
                            element={
                              <RouteSomeAccessSomePlatformBuilderModule
                                ACTIONS={["READ", "UPDATE"]}
                                modules={getAccessControlEnabledPLBModules().map(
                                  (d) => d.value
                                )}
                              >
                                <PlatformBuilder />
                              </RouteSomeAccessSomePlatformBuilderModule>
                            }
                          />

                          <Route
                            path="/platformBuilder/:view/:contentType"
                            element={
                              <RouteSomeAccessSomePlatformBuilderModule
                                ACTIONS={["READ", "UPDATE"]}
                                modules={getAccessControlEnabledPLBModules().map(
                                  (d) => d.value
                                )}
                              >
                                <PlatformBuilder />
                              </RouteSomeAccessSomePlatformBuilderModule>
                            }
                          />

                          {/* Direct Data Import Routes */}
                          <Route
                            path="/direct-data-import"
                            element={
                              <RouteAccessModule
                                moduleId={"templateBuilder"}
                                ACTION={["PUBLISH"]}
                              >
                                <DirectDataImport />
                              </RouteAccessModule>
                            }
                          />
                          <Route
                            path="/direct-data-import/:contentType"
                            element={
                              <RouteSomeAccessToContentType ACTION={["PUBLISH"]}>
                                <DirectDataImport isImportMoreDataMode />
                              </RouteSomeAccessToContentType>
                            }
                          />

                          {/* 404 Route */}
                          <Route path="*" element={
                            <PrivateRoute>
                              <OKE404 />
                            </PrivateRoute>
                            } />
                        </Routes>
                        </Suspense>
                        </ErrorBoundary>
                        }
                      
                    </div>
                    
                      <Footer />
                    
                  </Fragment>
                {enableCoachmark && userData?.data?.user && !warningScreen && (
                  <Coachmark />
                )}
              </OolibThemeProvider>
            </AppSettingsProvider>
          </Fragment>
        )}
      </PreAccessChecklistContextProvider>
    </BrowserRouter>
  );
};

export default Sentry.withProfiler(App);
